import { CircleAlert } from 'lucide-react'
import { Button } from '@teach/ui'
import type { ModerationState } from '@/features/moderation/types'
import { failedReviewText } from '@/features/moderation/lib/reviewText'
import { outcomeTexts } from '@/features/moderation/lib/reviewOutcome'
import { ReviewingUpdate } from '@/features/editor/components/publish/ReviewingUpdate'
import { ArticleViolationList } from './ArticleViolationList'
import { reviewMessages } from './reviewMessages'
import { useMessages } from '@/i18n'

interface Props {
  moderation: ModerationState
  /** Отзыв заявки — это `applyAccess('private')`, отдельной ручки нет. */
  onWithdraw: () => void
  /** Идёт запрос — двойной клик отзыва не шлёт второй `PATCH`. */
  disabled: boolean
}

const box = 'flex items-start gap-2.5 rounded-2xl border p-4 text-sm'

/** Плашка состояния проверки статьи над селектом доступа. */
export const ArticleReviewPanel = ({ moderation, onWithdraw, disabled }: Props) => {
  const r = useMessages(reviewMessages)
  const { review, visibility, hasUnsubmittedChanges } = moderation
  if (review?.status === 'pending') {
    // Как у курса: серая карточка с полоской вместо жёлтой плашки.
    const isUpdate = review.kind === 'update' && visibility !== 'unpublished'
    return (
      <ReviewingUpdate
        kind={review.kind}
        live={visibility !== 'unpublished'}
        title={isUpdate ? r.reviewingUpdateTitle : r.reviewingArticleTitle}
        body={isUpdate ? r.reviewingUpdateBody : r.reviewingArticleBody}
      >
        {/* У живой статьи «отзыв» снял бы её с публикации под видом отмены
            заявки — отзыв есть только у неопубликованной. */}
        {visibility === 'unpublished' ? (
          <Button variant="outline" size="sm" disabled={disabled} onClick={onWithdraw}>
            {r.withdraw}
          </Button>
        ) : null}
      </ReviewingUpdate>
    )
  }
  const texts = outcomeTexts(review, visibility, hasUnsubmittedChanges)
  if (texts === null) return null
  if (review?.status === 'rejected') {
    return (
      <div className="space-y-3">
        <div className={`${box} border-destructive/30 bg-destructive/10 text-destructive`}>
          <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" />
          <p className="font-medium">{texts.rejectedTitle}</p>
        </div>
        <ArticleViolationList violations={review.violations} />
        <p className="text-sm text-muted-foreground">{texts.rejectedHint}</p>
      </div>
    )
  }
  if (review?.status === 'failed') {
    return (
      <div className={`${box} border-warning/30 bg-warning/10 text-warning`}>
        <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" />
        <div className="space-y-1">
          <p className="font-medium">{failedReviewText(review.errorCode)}</p>
          {texts.retryHint ? <p>{texts.retryHint}</p> : null}
        </div>
      </div>
    )
  }
  return null
}
